import { useEffect, useMemo, useState } from "react";
import { api, apiErrorMessage } from "@/lib/api";
import { useProperty } from "@/contexts/PropertyContext";
import { PageHeader } from "@/components/PageHelpers";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { FileText, Users, Download, ShieldCheck, Search, Plus, Trash2, Mail, Sparkles } from "lucide-react";

export default function BoardArchivePage() {
  const { propertyId, properties } = useProperty();
  const [propFilter, setPropFilter] = useState("current");
  const [q, setQ] = useState("");
  const [items, setItems] = useState([]);
  const [downloading, setDownloading] = useState(null);

  const [members, setMembers] = useState([]);
  const [addOpen, setAddOpen] = useState(false);
  const [newMember, setNewMember] = useState({ name: "", email: "", role: "Board Member" });
  const [nudging, setNudging] = useState(false);

  const effectivePid = propFilter === "all" ? null : (propFilter === "current" ? propertyId : propFilter);

  const loadArchive = async () => {
    const params = new URLSearchParams();
    if (effectivePid) params.set("property_id", effectivePid);
    if (q.trim()) params.set("q", q.trim());
    try {
      const { data } = await api.get(`/reports/board-packet/history?${params.toString()}`);
      setItems(data.items || []);
    } catch { setItems([]); }
  };
  const loadMembers = async () => {
    const params = new URLSearchParams();
    if (effectivePid) params.set("property_id", effectivePid);
    try {
      const { data } = await api.get(`/board/members?${params.toString()}`);
      setMembers(data.items || []);
    } catch { setMembers([]); }
  };

  useEffect(() => { if (propertyId) { loadArchive(); loadMembers(); } }, [propertyId, propFilter]);
  useEffect(() => {
    const t = setTimeout(loadArchive, 200);
    return () => clearTimeout(t);
  }, [q]);

  const regenerate = async (item) => {
    setDownloading(item.id);
    try {
      const res = await api.post("/reports/board-packet/sign-multi", {
        property_id: item.property_id,
        signers: (item.signers || []).map((s) => ({ name: s.name, role: s.role })),
      }, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `board-packet-${item.id.slice(0,8)}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Ratified PDF regenerated");
    } catch { toast.error("Download failed"); }
    setDownloading(null);
  };

  const addMember = async () => {
    if (!newMember.name || !newMember.email) return toast.error("Name & email required");
    const targetPid = effectivePid || propertyId;
    if (!targetPid) return toast.error("Pick a property first");
    try {
      await api.post("/board/members", { ...newMember, property_id: targetPid });
      toast.success("Board member added");
      setAddOpen(false);
      setNewMember({ name: "", email: "", role: "Board Member" });
      loadMembers();
    } catch (e) { toast.error(apiErrorMessage(e)); }
  };

  const removeMember = async (id) => {
    if (!window.confirm("Remove this board member?")) return;
    try {
      await api.delete(`/board/members/${id}`);
      toast.success("Removed");
      loadMembers();
    } catch (e) { toast.error(apiErrorMessage(e)); }
  };

  const nudge = async () => {
    const targetPid = effectivePid || propertyId;
    if (!targetPid) return toast.error("Pick a property");
    setNudging(true);
    try {
      const { data } = await api.post("/board/nudge", { property_id: targetPid });
      if (data.skipped) {
        toast.success(`Nudge skipped — ${data.skipped}`);
      } else {
        toast.success(`Nudge sent · ${data.sent} delivered · ${data.queued} queued`);
      }
    } catch (e) { toast.error(apiErrorMessage(e)); }
    setNudging(false);
  };

  const totalArchived = items.length;

  return (
    <div>
      <PageHeader
        eyebrow="Governance"
        title="Board Archive"
        subtitle="Every ratified packet, every signer, every hash — searchable across your portfolio."
      />

      {/* Board members panel */}
      <Card className="border-white/10 bg-card p-5 mb-6">
        <div className="flex items-center justify-between gap-3 mb-3">
          <div>
            <div className="overline">Board Members</div>
            <div className="text-sm text-zinc-400">Monthly ratify reminders go to this list.</div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              data-testid="nudge-now"
              variant="outline"
              className="border-white/10"
              onClick={nudge}
              disabled={nudging}
            >
              <Sparkles className="h-4 w-4 mr-1" /> {nudging ? "Sending…" : "Nudge now"}
            </Button>
            <Dialog open={addOpen} onOpenChange={setAddOpen}>
              <DialogTrigger asChild>
                <Button data-testid="add-member" className="bg-indigo-600 hover:bg-indigo-700 rounded-full">
                  <Plus className="h-4 w-4 mr-1" /> Add member
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-card border-white/10">
                <DialogHeader><DialogTitle>Add board member</DialogTitle></DialogHeader>
                <div className="space-y-3">
                  <div><Label>Name</Label><Input data-testid="member-name" value={newMember.name} onChange={(e) => setNewMember({ ...newMember, name: e.target.value })} /></div>
                  <div><Label>Email</Label><Input data-testid="member-email" type="email" value={newMember.email} onChange={(e) => setNewMember({ ...newMember, email: e.target.value })} /></div>
                  <div><Label>Role</Label><Input data-testid="member-role" value={newMember.role} onChange={(e) => setNewMember({ ...newMember, role: e.target.value })} /></div>
                  <Button data-testid="member-save" onClick={addMember} className="w-full bg-indigo-600 hover:bg-indigo-700">Save</Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </div>
        {members.length === 0 ? (
          <div className="text-sm text-muted-foreground">No board members. Add one so ratify reminders can start firing.</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
            {members.map((m) => (
              <div key={m.id} className="rounded-md border border-white/10 bg-white/[0.02] p-3 flex items-start justify-between" data-testid={`member-${m.id}`}>
                <div className="min-w-0">
                  <div className="font-medium truncate">{m.name}</div>
                  <div className="text-xs text-zinc-500 truncate flex items-center gap-1">
                    <Mail className="h-3 w-3" /> {m.email}
                  </div>
                  <Badge variant="outline" className="border-white/10 mt-1">{m.role}</Badge>
                </div>
                <button onClick={() => removeMember(m.id)} data-testid={`remove-member-${m.id}`} className="text-zinc-500 hover:text-rose-400">
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-2 mb-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-500" />
          <Input
            data-testid="archive-search"
            placeholder="Search signers, property, ID, or hash…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="pl-9 bg-white/[0.03] border-white/10 text-white placeholder:text-zinc-500"
          />
        </div>
        <Select value={propFilter} onValueChange={setPropFilter}>
          <SelectTrigger className="w-60" data-testid="archive-property">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="current">Current property</SelectItem>
            <SelectItem value="all">All my properties</SelectItem>
            {properties.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div className="text-xs text-zinc-500 mb-3">{totalArchived} packet{totalArchived === 1 ? "" : "s"} archived</div>

      {items.length === 0 ? (
        <Card className="border-dashed border-white/10 p-10 text-center text-muted-foreground">
          Nothing matches. Change filters or sign a packet from the dashboard.
        </Card>
      ) : (
        <div className="space-y-3">
          {items.map((it) => (
            <Card key={it.id} className="border-white/10 bg-card p-5" data-testid={`archive-item-${it.id}`}>
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <div className="overline">Ratified Packet</div>
                    <Badge variant="outline" className="border-emerald-500/40 text-emerald-300 bg-emerald-500/10">
                      <ShieldCheck className="h-3 w-3 mr-1 inline" /> {(it.signers || []).length} signature{(it.signers || []).length === 1 ? "" : "s"}
                    </Badge>
                    <div className="text-xs text-zinc-500">{new Date(it.signed_at).toLocaleString()}</div>
                  </div>
                  <div className="text-lg font-semibold font-display mt-1 flex items-center gap-2">
                    <FileText className="h-4 w-4 text-orange-400" />
                    {it.property_name || "Board Packet"} · {it.id.slice(0,8)}
                  </div>
                  <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-2">
                    {(it.signers || []).map((s, idx) => (
                      <div key={idx} className="rounded-md border border-white/10 bg-white/[0.02] p-3">
                        <div className="text-[10px] uppercase tracking-widest text-zinc-500 flex items-center gap-1">
                          <Users className="h-3 w-3" /> Signature {idx + 1}
                        </div>
                        <div className="text-sm font-semibold mt-1">{s.name}</div>
                        <div className="text-xs text-zinc-400">{s.role}</div>
                        <div className="text-[10px] font-mono text-zinc-500 mt-1 break-all">
                          hash: {s.signature_hash?.slice(0, 20)}…
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="mt-3 text-[11px] font-mono text-zinc-500 break-all">
                    content_hash: {it.content_hash}
                  </div>
                  {it.created_by_name && (
                    <div className="text-xs text-zinc-500 mt-1">Prepared by {it.created_by_name}</div>
                  )}
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  data-testid={`archive-download-${it.id}`}
                  onClick={() => regenerate(it)}
                  disabled={downloading === it.id}
                  className="border-white/10 shrink-0"
                >
                  <Download className="h-4 w-4 mr-1" /> {downloading === it.id ? "…" : "Re-download"}
                </Button>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
